// UP6: the one place background-session events become a system notification,
// an in-app notice, a sound and a badge — each only when the person turned
// that channel on. Mounted once at the root (see the UP6 report's mount
// Request); renders nothing. The fold in ./sessionNotifications decides the
// events; this file only carries them to the chosen channels.
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ClientSettings, SessionNotificationRow } from "@cleo/contracts";

import { toastManager } from "~/components/ui/toast";
import { getClientSettings, useClientSettings } from "~/hooks/useSettings";
import { ensureLocalApi } from "~/localApi";
import { useThreadShells } from "~/state/entities";
import {
  WORLD_CONVERSATION_ENVIRONMENT,
  useWorldConversationNotificationRows,
} from "~/neucleos/worldConversationStrip";
import { setSessionNotificationBadge } from "./sessionNotificationBadge";
import {
  type SessionNotificationChannel,
  appendSessionNotificationLedger,
  ledgerEntryFor,
} from "./sessionNotificationLedger";
import {
  playSessionNotificationSound,
  soundForEvent,
  unlockSessionNotificationAudio,
} from "./sessionNotificationSound";
import { useShellStatuses } from "./shellStatuses";
import {
  SESSION_NOTIFICATION_TITLES,
  type SessionNotificationEvent,
  type SessionNotificationMark,
  deskIsFocused,
  detectSessionNotifications,
  hasNotificationSound,
  hasSystemNotifications,
  soundKindForEvent,
} from "./sessionNotifications";

const NOTIFICATION_SETTINGS = (settings: ClientSettings) => ({
  mode: settings.sessionNotifications,
  inApp: settings.sessionNotificationsInApp,
  badge: settings.sessionNotificationBadge,
  sound: settings.sessionNotificationSound,
});

type SystemNotificationCtor = new (
  title: string,
  options?: { body?: string; tag?: string; silent?: boolean },
) => { close(): void; addEventListener(type: "click", listener: () => void): void };

function systemNotificationCtor(): SystemNotificationCtor | null {
  const ctor = (globalThis as { Notification?: SystemNotificationCtor & { permission?: string } })
    .Notification;
  if (ctor === undefined || ctor.permission !== "granted") return null;
  return ctor;
}

export function SessionNotificationCoordinator() {
  const settings = useClientSettings(NOTIFICATION_SETTINGS);
  const enabled = settings.mode !== "off" || settings.inApp || settings.badge;
  return enabled ? <ArmedSessionNotifications {...settings} /> : null;
}

function ArmedSessionNotifications(props: ReturnType<typeof NOTIFICATION_SETTINGS>) {
  // W-b6b6e41b (Can 36): the person's conversations kept in the World, under the same rule and the same settings as the
  // Desk's own threads (one notice per conversation, helpers rolled up); their "shell" is the World's own list, always live.
  const desk = useThreadShells();
  const world = useWorldConversationNotificationRows();
  const threads = useMemo<ReadonlyArray<SessionNotificationRow>>(() => [...desk, ...world], [desk, world]);
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as {
    environmentId?: string;
    threadId?: string;
    sessionId?: string;
  };
  const environmentIds = useMemo(
    () => [...new Set(desk.map((thread) => thread.environmentId))].sort(),
    [desk],
  );
  const statuses = useShellStatuses(environmentIds);
  const marks = useRef<ReadonlyMap<string, SessionNotificationMark>>(new Map());
  const pending = useRef(new Map<string, { close(): void } | null>());

  const isActive = useCallback(
    (thread: SessionNotificationRow) =>
      (params.environmentId === thread.environmentId && params.threadId === thread.id) ||
      (params.sessionId !== undefined && thread.sessionId === params.sessionId),
    [params.environmentId, params.threadId, params.sessionId],
  );

  const openSession = useCallback(
    async (event: SessionNotificationEvent) => {
      try {
        await ensureLocalApi().shell.focusWindow();
      } catch {
        // A browser tab cannot always focus itself; the navigation still lands.
      }
      if (event.sessionId !== null && event.worldKey !== null) {
        await navigate({
          to: "/session/$sessionId",
          params: { sessionId: event.sessionId },
          search: { world: event.worldKey },
        });
        return;
      }
      await navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: event.environmentId, threadId: event.threadId },
      });
    },
    [navigate],
  );

  const publishBadge = useCallback(() => {
    if (!props.badge) return;
    void setSessionNotificationBadge(pending.current.size);
  }, [props.badge]);

  // Focus clears the badge and the pending system notices: the person is here.
  useEffect(() => {
    const clear = () => {
      for (const notice of pending.current.values()) notice?.close();
      pending.current.clear();
      void setSessionNotificationBadge(0);
    };
    if (!props.badge) {
      clear();
      return;
    }
    window.addEventListener("focus", clear);
    return () => {
      window.removeEventListener("focus", clear);
      clear();
    };
  }, [props.badge]);

  // Sound needs a person's gesture before the browser lets audio start.
  useEffect(() => {
    if (!hasNotificationSound(props.mode)) return;
    const unlock = () => void unlockSessionNotificationAudio();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [props.mode]);

  useEffect(() => {
    const result = detectSessionNotifications({
      threads,
      marks: marks.current,
      shellStatus: (environmentId) =>
        environmentId === WORLD_CONVERSATION_ENVIRONMENT ? "live" : (statuses.get(environmentId) ?? "empty"),
      isActive,
    });
    marks.current = result.marks;
    if (result.events.length === 0) return;
    const focused = deskIsFocused(document);
    for (const event of result.events) {
      const title = SESSION_NOTIFICATION_TITLES[event.kind];
      const reached: Array<SessionNotificationChannel> = [];
      if (hasNotificationSound(props.mode)) {
        reached.push("sound");
        void playSessionNotificationSound(
          soundForEvent(props.sound, soundKindForEvent(event.kind)),
          () => hasNotificationSound(getClientSettings().sessionNotifications),
        );
      }
      if (focused) {
        if (props.inApp) {
          reached.push("in-app");
          const toastId = toastManager.add({
            type:
              event.kind === "completed"
                ? "success"
                : event.kind === "failed"
                  ? "error"
                  : "warning",
            title,
            description: event.title,
            timeout: 8000,
            data: { hideCopyButton: true },
            actionProps: {
              children: "Open session",
              onClick: () => {
                toastManager.close(toastId);
                void openSession(event);
              },
            },
          });
        }
        if (reached.length > 0)
          void appendSessionNotificationLedger(ledgerEntryFor(event, reached));
        continue;
      }
      // Not focused: the system channel and the badge.
      let notice: { close(): void } | null = null;
      if (hasSystemNotifications(props.mode)) {
        const Ctor = systemNotificationCtor();
        if (Ctor !== null) {
          try {
            const created = new Ctor(title, { body: event.title, tag: event.tag, silent: true });
            created.addEventListener("click", () => {
              created.close();
              void openSession(event);
            });
            notice = created;
            reached.push("system");
          } catch {
            notice = null;
          }
        }
      }
      if (props.badge || notice !== null) {
        pending.current.get(event.tag)?.close();
        pending.current.set(event.tag, notice);
        if (props.badge) reached.push("badge");
        publishBadge();
      }
      if (reached.length > 0) void appendSessionNotificationLedger(ledgerEntryFor(event, reached));
    }
  }, [threads, statuses, isActive, props, openSession, publishBadge]);

  return null;
}
